import React, { useEffect, useState } from 'react';
import { PUBLIC_QR_TEST_GROUPS, buildTestQrImageUrl, FESTIVAL_DAY_OPTIONS } from './testQrScenarios';
import api from '../services/api';

const QrTestPage = () => {
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [timerStart, setTimerStart] = useState(null);
  const [resettingQrs, setResettingQrs] = useState(false);
  const [qrResetStatus, setQrResetStatus] = useState('');
  const [resettingAllQrs, setResettingAllQrs] = useState(false);
  const [simDay, setSimDay] = useState(FESTIVAL_DAY_OPTIONS[0].date);
  const [person, setPerson] = useState(1);

  const PERSON_OPTIONS = [1, 2, 3, 4, 5];
  const personCode = (code) => `TESTQR-P${person}-${String(code).replace(/^TESTQR-/, '')}`;

  useEffect(() => {
    if (!timerRunning || timerStart === null) return;

    let rafId;
    const tick = () => {
      setElapsedMs(Date.now() - timerStart);
      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [timerRunning, timerStart]);

  const formatElapsed = (ms) => {
    const safeMs = Math.max(0, ms || 0);
    const totalSeconds = Math.floor(safeMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const centiseconds = Math.floor((safeMs % 1000) / 10);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`;
  };

  const startTimer = () => {
    setTimerStart(Date.now() - elapsedMs);
    setTimerRunning(true);
  };

  const stopTimer = () => {
    setTimerRunning(false);
  };

  const resetTimer = () => {
    setTimerRunning(false);
    setElapsedMs(0);
    setTimerStart(null);
  };

  const resetQrStates = async () => {
    if (resettingQrs) return;

    setResettingQrs(true);
    setQrResetStatus('');
    try {
      await api.resetTestQrStates('', person);
      setQrResetStatus(`Person ${person} QRs reset`);
    } catch (error) {
      setQrResetStatus(error?.message || 'Failed to reset QRs');
    } finally {
      setResettingQrs(false);
    }
  };

  const resetAllQrStates = async () => {
    if (resettingAllQrs) return;

    setResettingAllQrs(true);
    setQrResetStatus('');
    try {
      // No person/code => backend clears scan state + attendance logs for ALL
      // 5 personas at once.
      await api.resetTestQrStates('', '');
      setQrResetStatus('All QRs reset (all 5 people)');
    } catch (error) {
      setQrResetStatus(error?.message || 'Failed to reset all QRs');
    } finally {
      setResettingAllQrs(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '5.25rem 0.75rem 1.75rem',
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)',
      }}
    >
      <div
        style={{
          position: 'fixed',
          top: 8,
          left: 0,
          right: 0,
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(15, 23, 42, 0.9)',
            color: '#f8fafc',
            borderRadius: 999,
            border: '1px solid rgba(148, 163, 184, 0.35)',
            padding: '0.4rem 0.7rem 0.4rem 0.75rem',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.28)',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontWeight: 800,
              fontSize: 17,
              minWidth: 120,
              textAlign: 'center',
              letterSpacing: '0.03em',
            }}
          >
            {formatElapsed(elapsedMs)}
          </div>
          <button
            onClick={startTimer}
            disabled={timerRunning}
            style={{
              border: 'none',
              background: timerRunning ? '#64748b' : '#16a34a',
              color: '#fff',
              borderRadius: 999,
              padding: '0.32rem 0.7rem',
              fontWeight: 700,
              cursor: timerRunning ? 'not-allowed' : 'pointer',
            }}
          >
            Start
          </button>
          <button
            onClick={stopTimer}
            disabled={!timerRunning}
            style={{
              border: 'none',
              background: !timerRunning ? '#64748b' : '#2563eb',
              color: '#fff',
              borderRadius: 999,
              padding: '0.32rem 0.7rem',
              fontWeight: 700,
              cursor: !timerRunning ? 'not-allowed' : 'pointer',
            }}
          >
            Stop
          </button>
          <button
            onClick={resetTimer}
            style={{
              border: '1px solid rgba(203, 213, 225, 0.45)',
              background: 'transparent',
              color: '#f8fafc',
              borderRadius: 999,
              padding: '0.32rem 0.7rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Reset Timer
          </button>
          <button
            onClick={resetQrStates}
            disabled={resettingQrs}
            style={{
              border: 'none',
              background: resettingQrs ? '#9ca3af' : '#dc2626',
              color: '#fff',
              borderRadius: 999,
              padding: '0.32rem 0.76rem',
              fontWeight: 800,
              cursor: resettingQrs ? 'not-allowed' : 'pointer',
            }}
          >
            {resettingQrs ? 'Resetting QRs...' : 'Reset QRs'}
          </button>
          <button
            onClick={resetAllQrStates}
            disabled={resettingAllQrs}
            title="Reset scan state + attendance logs for ALL 5 people"
            style={{
              border: 'none',
              background: resettingAllQrs ? '#9ca3af' : '#7f1d1d',
              color: '#fff',
              borderRadius: 999,
              padding: '0.32rem 0.76rem',
              fontWeight: 800,
              cursor: resettingAllQrs ? 'not-allowed' : 'pointer',
            }}
          >
            {resettingAllQrs ? 'Resetting ALL...' : 'Reset ALL QRs'}
          </button>
          {!!qrResetStatus && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: /reset$/i.test(qrResetStatus) ? '#86efac' : '#fecaca',
                padding: '0.1rem 0.35rem',
              }}
            >
              {qrResetStatus}
            </span>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              paddingLeft: 8,
              borderLeft: '1px solid rgba(148, 163, 184, 0.35)',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Scan day</span>
            {FESTIVAL_DAY_OPTIONS.map((opt) => (
              <button
                key={opt.date}
                onClick={() => setSimDay(opt.date)}
                style={{
                  border: 'none',
                  borderRadius: 999,
                  padding: '0.32rem 0.7rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: simDay === opt.date ? '#188cb8' : 'rgba(148, 163, 184, 0.25)',
                  color: simDay === opt.date ? '#fff' : '#e2e8f0',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              paddingLeft: 8,
              borderLeft: '1px solid rgba(148, 163, 184, 0.35)',
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>Person</span>
            {PERSON_OPTIONS.map((p) => (
              <button
                key={p}
                onClick={() => setPerson(p)}
                style={{
                  border: 'none',
                  borderRadius: 999,
                  padding: '0.32rem 0.7rem',
                  fontWeight: 800,
                  cursor: 'pointer',
                  background: person === p ? '#7c3aed' : 'rgba(148, 163, 184, 0.25)',
                  color: person === p ? '#fff' : '#e2e8f0',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1900, margin: '0 auto' }}>
        <div style={{ display: 'grid', gap: '1.1rem' }}>
          {PUBLIC_QR_TEST_GROUPS.map((group) => (
            <section
              key={group.key}
              style={{
                borderRadius: 22,
                border: '1px solid #dbe3f3',
                background: 'rgba(255,255,255,0.76)',
                boxShadow: '0 14px 28px rgba(15, 23, 42, 0.07)',
                padding: '0.8rem',
              }}
            >
              <div style={{ marginBottom: '0.65rem', textAlign: 'center' }}>
                <span
                  style={{
                    display: 'inline-block',
                    borderRadius: 999,
                    padding: '0.2rem 0.7rem',
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: '0.07em',
                    color: '#188cb8',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                  }}
                >
                  {group.title}
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: group.multiDay ? 'row' : 'column',
                  flexWrap: group.multiDay ? 'wrap' : 'nowrap',
                  gap: '6rem',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {group.rows.map((row) => (
                  <div
                    key={row.code}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <img
                      src={buildTestQrImageUrl(personCode(row.code), 900, simDay)}
                      alt={`QR test ticket - ${row.seatLabel || row.code}`}
                      style={{
                        width: 360,
                        maxWidth: '100%',
                        height: 'auto',
                        aspectRatio: '1 / 1',
                        borderRadius: 12,
                        display: 'block',
                        imageRendering: 'crisp-edges',
                      }}
                    />
                    {row.seatLabel && (
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#334155', textAlign: 'center' }}>
                        {row.seatLabel}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};

export default QrTestPage;
